import { startCase, uniq } from 'es-toolkit';
import React, { Fragment } from 'react';
import { connect, ResolveThunks } from 'react-redux';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { EMAIL_REGEX } from '@paragon/client-tools';
import {
  ComponentFileCollection,
  extractUploadedFilesFromZip,
  FileFromBrowserUpload,
  FileManager,
  FileManagerResult,
  MiscellaneousFileCollection,
  pFetchMetaData,
  processFiles,
} from '@paragon/component-processor';
import { JobPriority, JobType, Organization, PlateManufacturer, User } from '@paragon/contract-library';
import { createPermissionSelector } from '@paragon/login-tools';
import { enqueueError } from '@paragon/notification-tools';
import { KeyToValueOfType, naturalSortByProperty } from '@paragon/utilities';

import {
  attemptToOpenJob,
  insertJob,
  JobDataMessageWithoutId,
  RequiredAndNonNullable,
  updateJob,
  validatePdfs,
} from '../../actions/jobActions';
import { jobTypes } from '../../jobTypes';
import { getJobBeingEdited } from '../../selectors/getJobBeingEdited';
import { getSuggestions } from '../../selectors/getSuggestions';
import { RootState } from '../../store';
import { JobProperties, updateDraftJobProperties } from '../../store/jobsSlice';
import { OnShowDownloadPopupParams } from '../RootComponent';

type PropertyType = 'unique' | 'select' | 'text';

interface UndefaultedPropertyDefinition {
  readonly label: string;
  readonly key: keyof JobProperties;
  readonly type?: PropertyType;
  readonly required?: boolean;
  readonly defaultValue?: string | null;
}

const undefaultedJobProperties: readonly UndefaultedPropertyDefinition[] = [
  { label: 'Name', key: 'name', type: 'unique', required: true },
  { label: 'Regions', key: 'selectedRegions', type: 'select', required: true },
  { label: 'Job Type', key: 'selectedJobType', type: 'select', required: true },
  { label: 'Job Priority', key: 'selectedJobPriority', type: 'select', required: true },
  { label: 'Design Program', key: 'designProgram', type: 'unique', required: true },
  { label: 'Design Code', key: 'designCode', type: 'unique', defaultValue: null },
  { label: 'Plate Manufacturer', key: 'plateManufacturer', type: 'select', required: true },
  // special case with ignore button
  { label: 'Organizations', key: 'selectedOrganizations', type: 'select', required: true },
  { label: 'Comments', key: 'comments', type: 'unique', defaultValue: '' },
  { label: 'Contact Name', key: 'contactName', type: 'unique', required: true },
  { label: 'Contact Phone', key: 'contactPhone', type: 'unique', required: true },
  { label: 'Contact Email', key: 'contactEmail', type: 'unique', required: true },
  { label: 'Billing Location', key: 'billingLocation', type: 'select', required: true },
  { label: 'Lot Number', key: 'lotNumber' },
  { label: 'Address', key: 'address' },
  { label: 'City', key: 'city' },
  { label: 'Subdivision', key: 'subdivision' },
  { label: 'Model', key: 'model' },
  { label: 'Building Designer Name', key: 'buildingDesignerName' },
  { label: 'Building Designer Address', key: 'buildingDesignerAddress' },
  { label: 'Building Designer City', key: 'buildingDesignerCity' },
  { label: 'Building Designer State', key: 'buildingDesignerState' },
  { label: 'Building Designer License Number', key: 'buildingDesignerLicenseNumber' },
  { label: 'Wind Code', key: 'windCode' },
  { label: 'Wind Speed', key: 'windSpeed' },
  { label: 'Wind Exposure Category', key: 'windExposureCategory' },
  { label: 'Mean Roof Height', key: 'meanRoofHeight' },
  { label: 'Roof Load', key: 'roofLoad' },
  { label: 'Floor Load', key: 'floorLoad' },
  { label: 'Customer Name', key: 'customerName' },
];

const defaultPropertyDefinition = {
  label: 'DEFAULT',
  type: 'text' as const,
  required: false, // things are not required by default
  defaultValue: '-', // and instead of repeating the "-" we put it in once
};

interface PropertyDefinition {
  readonly label: string;
  readonly key: keyof JobProperties;
  readonly type: PropertyType;
  readonly required: boolean;
  readonly defaultValue: string | null;
}

const jobProperties: readonly PropertyDefinition[] = undefaultedJobProperties.map((propertyDefinition) => ({
  ...defaultPropertyDefinition,
  ...propertyDefinition,
}));

const organizationsWithTddOptionsAccess = [
  'Paragon Component Systems',
  'Clearspan Components Inc.',
  'Develop Organization A',
];

const doNotShareText = 'Do not share';

function sanitizeName(name: string | undefined) {
  if (name == null) return undefined;
  // Check name for bad characters if name is not empty
  let sanitizedName = name.trim();
  if (sanitizedName.match(/^[/%]/)) {
    // Problematic characters at beginning of name
    sanitizedName = sanitizedName.substring(1);
  }
  if (sanitizedName.match(/[/%]$/)) {
    // Problematic characters at end of name
    sanitizedName = sanitizedName.substring(0, sanitizedName.length - 1);
  }
  return sanitizedName;
}

function userToLabel(user: string | User) {
  return typeof user === 'string' ? user : `${user.firstName} ${user.lastName}`;
}

function isPropertyEmpty(value: string | boolean | readonly unknown[] | null | undefined) {
  if (value == null) return true;
  if (typeof value === 'boolean') return false;
  return value.length === 0;
}

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ResolveThunks<typeof actionCreators>;
interface OwnProps {
  organization: Organization | null;
  onShowFileManagerPopup: (fileManagerPopup: React.ReactNode) => void;
  closePopup: () => void;
  onShowDownloadPopup: (params: OnShowDownloadPopupParams) => void;
  user: User;
  contactPhone?: string;
  contactEmail?: string;
}
type Props = StateProps & DispatchProps & OwnProps;

interface State {
  selectedZip: string | null;
  convertToParagonTdds: boolean;
  missingProperties: readonly (keyof JobProperties)[];
  ignoreOrganization?: boolean;
  fileManagerData?: FileManagerResult;
  unzippedComponentDesigns?: ComponentFileCollection;
  unzippedMiscellaneousFiles?: MiscellaneousFileCollection;
  loadingFiles?: boolean;
  noDesigns?: boolean;
}

class UnconnectedCreateJob extends React.Component<Props, State> {
  state: State = {
    selectedZip: null,
    convertToParagonTdds: false,
    missingProperties: [],
  };

  onTextChanged = (
    property: KeyToValueOfType<JobProperties, string | null | undefined>,
    event: React.ChangeEvent<HTMLSelectElement | HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    this.props.updateDraftJobProperties({ [property as keyof JobProperties]: event.target.value });
  };

  billingHandleChange = (event: SelectChangeEvent<string>) => {
    this.props.updateDraftJobProperties({ billingLocation: event.target.value });
  };

  closeFileManagerOk = (fileManagerData: FileManagerResult) => {
    this.setState({ fileManagerData });
    this.props.closePopup();
  };

  closeFileManagerCancel = () => {
    this.setState({ selectedZip: null, unzippedMiscellaneousFiles: {}, unzippedComponentDesigns: {} });
    this.props.closePopup();
  };

  onShowFileManager = () => {
    this.props.onShowFileManagerPopup(
      <FileManager
        showOther
        componentDesigns={this.state.unzippedComponentDesigns!}
        miscellaneousFiles={this.state.unzippedMiscellaneousFiles!}
        requireComponentsOrOtherPdfs
        onSubmit={this.closeFileManagerOk}
        onCancel={this.closeFileManagerCancel}
      />,
    );
  };

  onSetFile: React.ChangeEventHandler<HTMLInputElement> = async (event) => {
    // On selection of file(s), read data
    const fileList = event.target.files;
    if (!fileList || fileList.length === 0) {
      return;
    }

    const rawZipFiles = Array.from(fileList).filter((file) => file.name.toLowerCase().includes('.zip'));
    if (rawZipFiles.length === 0) {
      this.props.enqueueError(
        'Error: Please upload a .zip file containing a .pdf, .tre, .ans, and .dxf file for each component (only .pdf and .tre are required).',
      );
      return;
    }
    if (rawZipFiles.length > 1) {
      this.props.enqueueError('Error: You can only select one zip file.');
      return;
    }

    const uploadedZipBase64 = await new FileFromBrowserUpload(rawZipFiles[0]).getContents();

    this.setState({
      selectedZip: uploadedZipBase64,
      loadingFiles: true,
    });

    try {
      const extractedFiles = await extractUploadedFilesFromZip(uploadedZipBase64);

      const pdfsAreValid = await this.props.validatePdfsInJobZip(extractedFiles);
      if (!pdfsAreValid) {
        this.setState({
          selectedZip: null,
          loadingFiles: false,
        });
        return;
      }

      const metaData = await pFetchMetaData(extractedFiles);
      this.props.updateDraftJobProperties(metaData);
      const processedFiles = await processFiles(extractedFiles, this.props.regions);

      // Update the Plate Manufacturer if that control is not visible to the user
      if (!organizationsWithTddOptionsAccess.includes(this.props.user.organization)) {
        this.props.updateDraftJobProperties({
          plateManufacturer: processedFiles.plateManufacturer || 'MiTek',
        });
      }

      this.setState(
        {
          unzippedComponentDesigns: processedFiles.componentDesigns,
          unzippedMiscellaneousFiles: processedFiles.miscellaneousFiles,
          loadingFiles: false,
        },
        this.onShowFileManager,
      );
    } catch {
      this.setState({
        selectedZip: null,
        loadingFiles: false,
      });
      this.props.enqueueError('Error processing zip file.');
    }
  };

  onOpenJob: React.MouseEventHandler<HTMLButtonElement> = async () => {
    await this.props.attemptToOpenJob(this.props.draftJob!.jobBeingEditedId!);
  };

  onDownloadJob = () => {
    if (!this.props.jobBeingEdited) {
      console.error('Could not find job being edited');
      return;
    }
    const numMiscFiles = this.state.fileManagerData
      ? Object.keys(this.state.fileManagerData.miscellaneousFiles).length
      : this.props.jobBeingEdited.numberOfMiscFiles;
    const miscFilesButtonVisible = numMiscFiles > 0;
    this.props.onShowDownloadPopup({
      jobSummary: this.props.jobBeingEdited,
      jobId: this.props.jobBeingEdited.id,
      miscFilesButtonVisible,
      originalZipUrl: this.props.jobBeingEdited.originalZipUrl,
      separatedPdfsUrl: this.props.jobBeingEdited.separatedPdfsUrl,
      mergedPdfsUrl: this.props.jobBeingEdited.mergedPdfsUrl,
    });
  };

  onSaveClick = () => {
    const sanitizedName = sanitizeName(this.props.draftJob!.properties.name);

    const missingProperties = jobProperties
      .filter((property) => {
        if (property.key === 'name') {
          if (sanitizedName == null) return true;
          // Check name for bad characters if name is not empty
          if (sanitizedName.match(/[/%]/)) {
            // Problematic characters in middle of name
            this.props.enqueueError('A job name cannot contain the following characters: /, %');
            return true;
          }
        }

        if (property.key === 'contactEmail' && this.props.draftJob?.properties.contactEmail) {
          for (const emailAddress of this.props.draftJob.properties.contactEmail.split(/\s*,\s*/)) {
            if (!EMAIL_REGEX.test(emailAddress)) {
              return true;
            }
          }
        }

        if (property.required && isPropertyEmpty(this.props.draftJob!.properties[property.key])) {
          // For selectedOrganizations, an empty array means do not share
          if (
            property.key === 'selectedOrganizations' &&
            Array.isArray(this.props.draftJob!.properties[property.key])
          ) {
            return false;
          }
          return true;
        }
        return false;
      })
      .map((property) => property.key);

    this.setState((state: State) => ({
      missingProperties,
      noDesigns: !this.props.jobBeingEdited && state.selectedZip == null,
    }));
    const noDesigns = !this.props.jobBeingEdited && this.state.selectedZip == null;
    const needsSealableDesigns =
      !this.props.jobBeingEdited &&
      this.props.draftJob!.properties.selectedJobType === 'Seal' &&
      Object.keys(this.state.fileManagerData!.componentDesigns).length === 0 &&
      Object.keys(this.state.fileManagerData!.metaPdfs).length === 0;
    if (missingProperties.length !== 0) this.props.enqueueError('Missing or invalid input');
    if (needsSealableDesigns) this.props.enqueueError('No designs to seal in provided .ZIP');
    if (missingProperties.length !== 0 || needsSealableDesigns || noDesigns) return;

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const jobPropertyEntries: RequiredAndNonNullable<JobProperties> = Object.fromEntries(
      jobProperties.map((property) => {
        const initialValue = this.props.draftJob!.properties[property.key];
        const trimmedValue = typeof initialValue === 'string' ? initialValue.trim() : initialValue;
        const value = isPropertyEmpty(trimmedValue) ? property.defaultValue : trimmedValue;
        return [property.key, value] as const;
      }),
    );

    const jobDataMessage: JobDataMessageWithoutId = {
      ...jobPropertyEntries,
      name: sanitizedName!,
      regions: this.props.draftJob!.properties.selectedRegions.map((region) => region.name),
      selectedZip: this.state.selectedZip!,
      fileManagerData: this.state.fileManagerData!,
      jobType: this.props.draftJob!.properties.selectedJobType!,
      jobPriority: this.props.draftJob!.properties.selectedJobPriority,
      sharedWithOrganizations: this.props.draftJob!.properties.selectedOrganizations!,
      contactName: this.props.draftJob!.properties.contactName!,
      contactPhone: this.props.draftJob!.properties.contactPhone!,
      contactEmail: this.props.draftJob!.properties.contactEmail!,
      billingLocation: this.props.draftJob!.properties.billingLocation!,
      convertToParagonTdds: this.state.convertToParagonTdds,
      convertVerticalsUnderFifteenAndAHalfInchesToSpfNumberTwo:
        this.props.draftJob!.properties.convertVerticalsUnderFifteenAndAHalfInchesToSpfNumberTwo!,
    };

    this.commitJobValues(jobDataMessage);
  };

  isMissing(propertyName: keyof JobProperties) {
    return this.state.missingProperties.includes(propertyName);
  }

  createTextbox(
    propertyName: KeyToValueOfType<JobProperties, string | null | undefined>,
    required: boolean,
    name: string,
    missing: boolean,
    options?: { tooltip?: string },
  ) {
    const inputValue =
      this.props.draftJob!.properties[propertyName as keyof JobProperties] == null
        ? ''
        : (this.props.draftJob!.properties[propertyName as keyof JobProperties] as string);

    let field = (
      <TextField
        size="small"
        sx={{ width: 400 }}
        id={propertyName}
        label={name}
        onChange={(event) => this.onTextChanged(propertyName, event)}
        value={inputValue}
        variant="outlined"
        error={missing}
        required={required}
      />
    );

    if (options?.tooltip) {
      field = (
        <Tooltip disableInteractive title={options.tooltip}>
          {field}
        </Tooltip>
      );
    }

    return field;
  }

  commitJobValues = (jobValues: JobDataMessageWithoutId) => {
    if (this.props.jobBeingEdited) {
      this.props.updateJob({
        ...jobValues,
        id: this.props.jobBeingEdited.id,
      });
    } else {
      this.props.insertJob(jobValues);
    }
  };

  render() {
    if (!this.props.draftJob) return null;
    const jobID = this.props.draftJob.jobBeingEditedId != null ? 'Job ID:' : '';
    const jobIDNumber = this.props.draftJob.jobBeingEditedId != null ? `#${this.props.draftJob.jobBeingEditedId}` : '';
    const jobIDAndNameInput =
      this.props.draftJob.jobBeingEditedId != null ? (
        <Stack spacing={2}>
          <TextField
            size="small"
            sx={{ width: 400 }}
            id="outlined-read-only-input"
            label={jobID}
            value={jobIDNumber}
            slotProps={{ input: { readOnly: true } }}
            variant="outlined"
          />
          {this.createTextbox('name', true, 'Name', this.isMissing('name'))}
        </Stack>
      ) : (
        this.createTextbox('name', true, 'Name', this.isMissing('name'))
      );

    const uploadComponent = !this.props.jobBeingEdited ? (
      this.state.loadingFiles === true ? (
        <CircularProgress color="secondary" />
      ) : (
        <Grid>
          <label htmlFor="upload-zip">
            <Box
              component="input"
              sx={{ display: 'none' }}
              id="upload-zip"
              type="file"
              accept=".zip"
              onChange={this.onSetFile}
              multiple
            />
            <Button
              sx={this.state.noDesigns ? { m: 1, width: 200, borderColor: 'error.main' } : { m: 1, width: 200 }}
              variant="outlined"
              component="span"
            >
              Choose a .zip file
            </Button>
          </label>
        </Grid>
      )
    ) : (
      ''
    );

    let designsSelectedText;
    if (this.state.selectedZip != null && this.state.fileManagerData != null) {
      const numDesigns = Object.keys(this.state.fileManagerData.componentDesigns).length;
      const numOther = Object.keys(this.state.fileManagerData.metaPdfs).length;
      const numMiscFiles = Object.keys(this.state.fileManagerData.miscellaneousFiles).length;
      designsSelectedText = `Components: ${numDesigns}; Other PDFs to seal: ${numOther}; Miscellaneous files: ${numMiscFiles}`;
    } else if (this.props.jobBeingEdited) {
      designsSelectedText = `Components: ${this.props.jobBeingEdited.numberOfDesigns}; Other PDFs to seal: ${this.props.jobBeingEdited.numberOfMetaPdfs}; Miscellaneous files: ${this.props.jobBeingEdited.numberOfMiscFiles}`;
    } else {
      designsSelectedText = 'No components selected';
    }

    const interfaceButtons = [];
    if (this.props.jobBeingEdited) {
      interfaceButtons.push(
        <Button
          sx={{ m: 1, width: 200 }}
          variant="outlined"
          id="saveChangesButton"
          key="save"
          onClick={this.onSaveClick}
        >
          Save Changes
        </Button>,
      );
      interfaceButtons.push(
        <Button
          sx={{ m: 1, width: 200 }}
          variant="outlined"
          id="openDownloadOptions"
          key="download"
          onClick={this.onDownloadJob}
        >
          Download
        </Button>,
      );

      if (this.props.canAccessMarkupClient) {
        interfaceButtons.push(
          <Button
            sx={{ m: 1, width: 200 }}
            variant="outlined"
            id="openJobPdfEditor"
            key="open"
            onClick={this.onOpenJob}
          >
            Open Job
          </Button>,
        );
      }
    } else {
      interfaceButtons.push(
        <Button sx={{ m: 1, width: 200 }} variant="outlined" id="createJob" key="create" onClick={this.onSaveClick}>
          Create Job
        </Button>,
      );
    }

    let contactUserOptions: (string | User)[] = [];
    if (this.props.usersInOrganization) {
      contactUserOptions = [...this.props.usersInOrganization];
    }
    const contactNamesInOrganization = contactUserOptions.map(userToLabel);
    const contactNamesNotInOrganizationButExistOnOtherJobs = this.props.suggestions.contactNames.filter(
      (contactName) => !contactNamesInOrganization.includes(contactName),
    );
    contactUserOptions = contactUserOptions.concat(contactNamesNotInOrganizationButExistOnOtherJobs);
    contactUserOptions = naturalSortByProperty(contactUserOptions, userToLabel);

    let contactEmailOptions = this.props.suggestions.contactEmails;
    if (this.props.usersInOrganization) {
      contactEmailOptions = uniq(contactEmailOptions.concat(this.props.usersInOrganization.map((user) => user.email)));
    }
    contactEmailOptions = naturalSortByProperty(contactEmailOptions, (email) => email);

    let billingLocation;
    if (!this.props.jobBeingEdited || this.props.jobBeingEdited.ownerOrganization === this.props.user.organization) {
      billingLocation = (
        <FormControl
          variant="outlined"
          sx={{ width: 400 }}
          size="small"
          required
          error={this.isMissing('billingLocation')}
        >
          <InputLabel id="billing-location-label">Billing Location</InputLabel>
          <Select
            label="Billing Location"
            inputProps={{
              name: 'billing location',
              id: 'billing-location-label',
            }}
            value={this.props.draftJob.properties.billingLocation ?? ''}
            onChange={this.billingHandleChange}
          >
            {this.props.organization
              ? this.props.organization.locations.map((location) => (
                  <MenuItem value={location} key={location}>
                    {location}
                  </MenuItem>
                ))
              : []}
          </Select>
        </FormControl>
      );
    }

    const plateManufacturerSelector = (
      <FormControl
        variant="outlined"
        sx={{ width: 400 }}
        size="small"
        required
        error={this.isMissing('plateManufacturer')}
        disabled={this.props.jobBeingEdited !== null}
      >
        <InputLabel id="plate-manufacturer-label-a">Plate Manufacturer</InputLabel>
        <Select
          label="Plate Manufacturer"
          inputProps={{
            name: 'plate manufacturer',
            id: 'plate-manufacturer-a',
          }}
          value={this.props.draftJob.properties.plateManufacturer ?? ''}
          onChange={(event) =>
            void this.props.updateDraftJobProperties({ plateManufacturer: event.target.value as PlateManufacturer })
          }
        >
          <MenuItem key="noneSelected" value="null" disabled>
            None selected
          </MenuItem>
          <MenuItem key="MiTek" value="MiTek">
            MiTek
          </MenuItem>
          <MenuItem key="Alpine" value="Alpine">
            Alpine
          </MenuItem>
          <MenuItem key="Eagle" value="Eagle">
            Eagle
          </MenuItem>
          <MenuItem key="Simpson" value="Simpson">
            Simpson
          </MenuItem>
          <MenuItem key="CH Machine" value="CH Machine">
            CH Machine
          </MenuItem>
        </Select>
      </FormControl>
    );

    let tddOptions;
    if (!this.props.jobBeingEdited && organizationsWithTddOptionsAccess.includes(this.props.user.organization)) {
      const tddOptionsDisabled = this.props.draftJob.properties.selectedJobType !== 'Seal';
      tddOptions = (
        <FormControl component="fieldset" size="small">
          <FormLabel component="legend">TDD Options</FormLabel>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={this.state.convertToParagonTdds}
                  onChange={() =>
                    this.setState((state: State) => ({ convertToParagonTdds: !state.convertToParagonTdds }))
                  }
                  name="convertToParagonTdds"
                  disabled={tddOptionsDisabled}
                />
              }
              label="Convert to Paragon TDDs"
            />
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={this.props.draftJob.properties.convertVerticalsUnderFifteenAndAHalfInchesToSpfNumberTwo}
                  onChange={() =>
                    void this.props.updateDraftJobProperties({
                      convertVerticalsUnderFifteenAndAHalfInchesToSpfNumberTwo:
                        !this.props.draftJob!.properties.convertVerticalsUnderFifteenAndAHalfInchesToSpfNumberTwo,
                    })
                  }
                  name="convertVerticalsUnderFifteenAndAHalfInchesToSpfNumberTwo"
                  disabled={tddOptionsDisabled || !this.state.convertToParagonTdds}
                />
              }
              label="Convert all vertical members under 15.5 inches to Spruce-Pine-Fir #2"
            />
          </FormGroup>
        </FormControl>
      );
    }

    const { selectedOrganizations } = this.props.draftJob.properties;
    const isDoNotShareSelected = selectedOrganizations && selectedOrganizations.length === 0;

    return (
      <>
        <Paper
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            m: 'auto',
            width: '50%',
            justifyContent: 'center',
            bgcolor: 'background.default',
          }}
        >
          {uploadComponent}
          {interfaceButtons}
        </Paper>
        <Grid container direction="row" sx={{ my: 2, justifyContent: 'space-evenly', alignItems: 'flex-start' }}>
          <Grid size={6}>
            <Stack spacing={2} sx={{ justifyContent: 'flex-start', alignItems: 'center' }}>
              {jobIDAndNameInput}
              <Typography>{designsSelectedText}</Typography>
              <Autocomplete
                multiple
                size="small"
                options={this.props.regions}
                getOptionLabel={(option) => option.name}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    sx={{ width: 400 }}
                    label="Select Region(s)"
                    required
                    error={this.isMissing('selectedRegions')}
                  />
                )}
                value={this.props.draftJob.properties.selectedRegions}
                onChange={(event, value) => {
                  this.props.updateDraftJobProperties({ selectedRegions: value });
                }}
              />
              <FormControl
                variant="outlined"
                sx={{ width: 400 }}
                size="small"
                required
                error={this.isMissing('selectedJobType')}
              >
                <InputLabel id="select-job-type">Select Job Type</InputLabel>
                <Select
                  label="Select Job Type"
                  value={this.props.draftJob.properties.selectedJobType ?? ''}
                  variant="outlined"
                  inputProps={{
                    name: 'select job type',
                    id: 'select-job-type',
                  }}
                  onChange={(event) => {
                    const selectedJobType = event.target.value as JobType;
                    this.props.updateDraftJobProperties({ selectedJobType });
                  }}
                >
                  {jobTypes.map((t) => (
                    <MenuItem key={t} value={t}>
                      {startCase(t)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl
                variant="outlined"
                sx={{ width: 400 }}
                size="small"
                required
                error={this.isMissing('selectedJobPriority')}
              >
                <InputLabel id="job-priority-label">Select Priority</InputLabel>
                <Select
                  label="Select Priority"
                  value={this.props.draftJob.properties.selectedJobPriority}
                  inputProps={{
                    name: 'select priority',
                    id: 'job-priority-label',
                  }}
                  onChange={(event) => {
                    this.props.updateDraftJobProperties({ selectedJobPriority: event.target.value as JobPriority });
                  }}
                >
                  {['Low', 'Normal', 'High'].map((t) => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {this.createTextbox('designProgram', true, 'Design Program', this.isMissing('designProgram'))}
              {this.createTextbox('designCode', false, 'Design Code', this.isMissing('designCode'))}
              {plateManufacturerSelector}
              <Autocomplete
                size="small"
                multiple={!isDoNotShareSelected}
                options={this.props.organizations.concat(doNotShareText)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    sx={{ width: 400 }}
                    label="Share With"
                    required
                    error={this.isMissing('selectedOrganizations') && !this.state.ignoreOrganization}
                  />
                )}
                value={
                  isDoNotShareSelected ? doNotShareText : (this.props.draftJob.properties.selectedOrganizations ?? [])
                }
                onChange={(event, value) => {
                  if (Array.isArray(value)) {
                    // Multiselect on
                    if (value.length === 0) {
                      // The x was hit to clear all
                      this.props.updateDraftJobProperties({ selectedOrganizations: null });
                    } else if (value.some((organization) => organization === doNotShareText)) {
                      // "Do not share" is selected
                      this.props.updateDraftJobProperties({ selectedOrganizations: [] });
                    } else {
                      // An organization was selected
                      this.props.updateDraftJobProperties({
                        selectedOrganizations: value,
                      });
                    }
                  } else {
                    // Multiselect off
                    if (value == null) {
                      // The x was hit to clear all
                      this.props.updateDraftJobProperties({ selectedOrganizations: null });
                    } else if (value !== doNotShareText) {
                      // "Do not share" was selected but now an organization is selected
                      this.props.updateDraftJobProperties({ selectedOrganizations: [value] });
                    }
                  }
                }}
              />
              <TextField
                sx={{ width: 400 }}
                id="commentsEditJob"
                label="Job Comments"
                multiline
                rows={4}
                value={this.props.draftJob.properties.comments}
                onChange={(e) => this.onTextChanged('comments', e)}
                variant="outlined"
                disabled={
                  !!this.props.jobBeingEdited && this.props.user.email !== this.props.jobBeingEdited.creatorEmail
                }
              />
              <Autocomplete
                sx={{ width: 400 }}
                size="small"
                options={contactUserOptions}
                freeSolo
                handleHomeEndKeys
                selectOnFocus
                getOptionLabel={userToLabel}
                renderOption={(props, user) => (
                  <li {...props} key={typeof user === 'string' ? user : user.email}>
                    {userToLabel(user)}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Contact Name *"
                    error={this.isMissing('contactName')}
                    // Save the custom value on blur since Autocomplete doesn't do this by default
                    onBlur={(event) => this.props.updateDraftJobProperties({ contactName: event.target.value })}
                  />
                )}
                value={this.props.draftJob!.properties.contactName ?? ''}
                onChange={(event, user) => {
                  typeof user === 'string'
                    ? this.props.updateDraftJobProperties({ contactName: userToLabel(user) })
                    : user
                      ? this.props.updateDraftJobProperties({
                          contactName: userToLabel(user),
                          contactPhone: user.phone,
                          contactEmail: user.email,
                        })
                      : this.props.updateDraftJobProperties({ contactName: undefined });
                }}
              />
              {this.createTextbox('contactPhone', true, 'Contact Phone', this.isMissing('contactPhone'))}
              <Tooltip
                disableInteractive
                title="Enter a comma-separated list of email addresses to send the job return email to all of them, e.g. userA@paragontruss.com, userB@paragontruss.com"
                placement="right"
              >
                <Autocomplete
                  size="small"
                  options={contactEmailOptions}
                  freeSolo
                  handleHomeEndKeys
                  selectOnFocus
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      sx={{ width: 400 }}
                      label="Contact Email"
                      required
                      error={this.isMissing('contactEmail')}
                      // Save the custom value on blur since Autocomplete doesn't do this by default
                      onBlur={(event) => this.props.updateDraftJobProperties({ contactEmail: event.target.value })}
                    />
                  )}
                  value={this.props.draftJob.properties.contactEmail}
                  onChange={(event, value) => {
                    this.props.updateDraftJobProperties({ contactEmail: value ?? undefined });
                  }}
                />
              </Tooltip>
              {billingLocation}
              {tddOptions}
            </Stack>
          </Grid>
          <Grid size={6}>
            <Stack spacing={2} sx={{ justifyContent: 'flex-start', alignItems: 'center' }}>
              {jobProperties
                .filter((p) => p.type === 'text')
                .map((prop) => (
                  <Fragment key={prop.key}>
                    {this.createTextbox(
                      prop.key as KeyToValueOfType<JobProperties, string | null | undefined>,
                      false,
                      prop.label,
                      false,
                    )}
                  </Fragment>
                ))}
            </Stack>
          </Grid>
        </Grid>
      </>
    );
  }
}

const hasMarkupAccessPermission = createPermissionSelector('markup-access');

const mapStateToProps = (state: RootState) => ({
  draftJob: state.jobs.draftJob,
  jobBeingEdited: getJobBeingEdited(state),
  organization: state.organizations.userOrganization,
  organizations: state.organizations.visibleOrganizations,
  regions: state.regions.regions,
  user: state.logInData.loggedInUser!,
  usersInOrganization: state.organizations.usersInOrganization,
  suggestions: getSuggestions(state),
  canAccessMarkupClient: hasMarkupAccessPermission(state),
});

const actionCreators = {
  attemptToOpenJob,
  updateDraftJobProperties,
  enqueueError,
  updateJob,
  insertJob,
  validatePdfsInJobZip: validatePdfs,
};

export default connect(mapStateToProps, actionCreators)(UnconnectedCreateJob);
